import axios from 'axios';
import { attachAuth } from '@/utils/authSession';

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || "http://localhost:9000/",
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
});

const apiAuthClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || "http://localhost:9000",
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
});

// Sends JWT when available but does NOT reject unauthenticated requests
const apiOptionalAuthClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || "http://localhost:9000",
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
});

const normalizeTimeAgoValue = (value) => {
  if (typeof value !== 'string') return value;
  return value.replace(/-\d+\s+seconds ago/i, 'seconds ago');
};

const normalizeTimeAgoDeep = (payload) => {
  if (Array.isArray(payload)) {
    payload.forEach(normalizeTimeAgoDeep);
    return payload;
  }

  if (payload && typeof payload === 'object') {
    Object.keys(payload).forEach((key) => {
      if (key === 'timeAgo') {
        payload[key] = normalizeTimeAgoValue(payload[key]);
      } else {
        normalizeTimeAgoDeep(payload[key]);
      }
    });
  }

  return payload;
};

const applyTimeAgoSanitizer = (response) => {
  if (response && response.data) {
    normalizeTimeAgoDeep(response.data);
  }
  return response;
};

apiClient.interceptors.response.use(applyTimeAgoSanitizer);
apiAuthClient.interceptors.response.use(applyTimeAgoSanitizer);
apiOptionalAuthClient.interceptors.response.use(applyTimeAgoSanitizer);

attachAuth(apiOptionalAuthClient);
attachAuth(apiAuthClient, { optional: false });


export default {

    getUser(){
      return apiAuthClient.get('/whoami')
    },

    getUsername(){
      return apiAuthClient.get('/whoami/name')
    },

    likeAPost(like, likedPost){
        return apiAuthClient.post(`/userpreferences/likePost/${likedPost}`, like)
    },
    checkPostLike(){
      return apiAuthClient.get(`/userpreferences/likePostCheck`)
    },
    unlikeAPost(likedPost){
      return apiAuthClient.post(`/userpreferences/unlikePost/${likedPost}`)
    },


    dislikeAPost(dislike, dislikedPost){
      return apiAuthClient.post(`/userpreferences/dislikePost/${dislikedPost}`, dislike)
    },
    undislikeAPost(dislikedPost){
      return apiAuthClient.post(`/userpreferences/undislikePost/${dislikedPost}`)
    },
    listDislikes(){
      return apiAuthClient.get(`/userpreferences/dislikes`)
    },

    saveForLater(saveForLater, saved){
      return apiAuthClient.post(`/userpreferences/saveForLater/${saved}`, saveForLater)
    },
    removeSaveForLater(saved){
      return apiAuthClient.post(`/userpreferences/removeFromSaveForLater/${saved}`)
    },
    listSaveForLaters(){
      return apiAuthClient.get(`/userpreferences/saveForLaters`)
    },
    getPoliticalBias(articleId){
      return apiClient.get(`/get/politics/score/${articleId}`)
    },
    getArticleSentiment(articleId){
      return apiClient.get(`/get/article/sentiment/${articleId}`)
    },
    getCoverageSpread(articleId) {
      return apiOptionalAuthClient.get(`/articles/${articleId}/coverage-spread`);
    },
    getTrendingStoryClusters({ category = null, hours = 24, limit = 10 } = {}) {
      const params = { hours, limit };
      if (category) params.category = category;
      return apiOptionalAuthClient.get('/story-clusters/trending', { params });
    },
    getStoryClusterDetail(clusterId) {
      return apiOptionalAuthClient.get(`/story-clusters/${clusterId}`);
    },
    getArticleFreeSummary(articleId){
      return apiClient.get(`/get/article/summary/${articleId}`)
    },
    getArticlePremiumInsight(articleId){
      return apiAuthClient.get(`/get/article/premium-insight/${articleId}`)
    },
    getBarbershopTake(articleId){
      return apiOptionalAuthClient.get(`/articles/${articleId}/barbershop-take`)
    },
    postBiasVote(articleId, payload){
      return apiAuthClient.post(`/articles/${articleId}/bias-vote`, payload)
    },
    deleteBiasVote(articleId){
      return apiAuthClient.delete(`/articles/${articleId}/bias-vote`)
    },
    getSentimentPulse(category) {
      const params = category ? { category } : {};
      return apiOptionalAuthClient.get('/politics/sentiment/pulse', { params });
    },

    followTopic(tag) {
      return apiAuthClient.post(`/topics/follow?tag=${encodeURIComponent(tag)}`);
    },

    unfollowTopic(tag) {
      return apiAuthClient.delete(`/topics/follow?tag=${encodeURIComponent(tag)}`);
    },

    getMyTopicsFeed() {
      return apiAuthClient.get('/me/topics/feed');
    },

    getFollowedTopics() {
      return apiAuthClient.get('/me/followed-topics');
    },

    getBlindspots(days = 7, perspective = null) {
      const params = { days };
      if (perspective) params.perspective = perspective;
      return apiClient.get('/politics/blindspot', { params });
    },
  }